import React, { useState, useEffect, useCallback } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography } from '@mui/material';
import { useDebounce } from '../../hooks/useDebounce';
import { getDevOptions } from '../../services/calendar.service';
import type { DevClientOption } from '../../services/calendar.service';

interface ClientAutocompleteProps {
  value: DevClientOption | null;
  onChange: (client: DevClientOption | null) => void;
  label?: string;
  helperText?: string;
}

export const ClientAutocomplete: React.FC<ClientAutocompleteProps> = ({
  value,
  onChange,
  label = 'Клієнт',
  helperText = 'Почніть вводити ім\'я або email. Обов\'язково — без вибраного клієнта відправка пакету недоступна.',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<DevClientOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedSearchTerm = useDebounce(inputValue, 500);

  const fetchSuggestions = useCallback(async (search: string) => {
    setLoading(true);
    try {
      const { clients } = await getDevOptions(search || undefined);
      setOptions(clients);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuggestions(debouncedSearchTerm);
  }, [debouncedSearchTerm, fetchSuggestions]);

  useEffect(() => {
    if (value) {
      setInputValue(`${value.name} (${value.email})`);
    } else {
      setInputValue('');
    }
  }, [value]);

  return (
    <Autocomplete
      id="dev-client-autocomplete"
      value={value}
      onChange={(_event, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setInputValue(newInputValue);
        } else if (reason === 'reset' && value) {
          setInputValue(`${value.name} (${value.email})`);
        }
      }}
      options={options}
      loading={loading}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      getOptionLabel={(option) => (option.name ? `${option.name} (${option.email})` : '')}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box>
            <Typography variant="body2">{option.name} <Typography component="span" variant="caption" color="text.secondary">#{option.id}</Typography></Typography>
            <Typography variant="caption" color="text.secondary">{option.email}</Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
